import React, { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Grid, Chip, TextField, Box } from '@mui/material';

import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import debounce from 'lodash/debounce';
import AddIcon from '@mui/icons-material/Add';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';

import PagedTable from 'components/table/PagedTable';
import { SPACES_LIST_PAGED_HIERARCHICAL_QUERY } from '../gql';
import { CountryName } from 'components/Language';
import { userSelector } from 'helper/security';

/**
 * Spaces list page with hierarchical display and debounced search.
 * Shows spaces in a tree structure with parent-child relationships.
 */
export default function SpacesList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const user = userSelector()!;

  // Debounce search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debounceSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
      }, 500),
    [],
  );

  useEffect(() => {
    debounceSearch(searchTerm);
  }, [searchTerm, debounceSearch]);

  // Use lazy query for pagination
  const [query, { data, loading, error }] = useLazyQuery(SPACES_LIST_PAGED_HIERARCHICAL_QUERY, {
    fetchPolicy: 'network-only',
  });

  return (
    <>
      <Helmet>
        <title>{i18next.t('spaces-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
            <SearchIcon />
            <TextField
              label={i18next.t('search')}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={String(i18next.t('spaces-search-placeholder'))}
              sx={{
                minWidth: 300,
                '& .MuiInputBase-input': { py: 0.5 },
              }}
            />
          </Box>
          <PagedTable
            filterKey={debouncedSearchTerm}
            refetchPage={(skip, take) =>
              query({
                variables: {
                  skip,
                  take,
                  search: debouncedSearchTerm.trim().length >= 2 ? debouncedSearchTerm : undefined,
                },
              })
            }
            headers={[
              i18next.t('spaces-list-header-name'),
              i18next.t('spaces-list-header-country'),
              i18next.t('spaces-list-header-users'),
              i18next.t('spaces-list-header-license'),
            ]}
            rows={
              data?.listSpacesPagedHierarchical.spaces.map(item => [
                <Link to={`/settings/spaces/${item.space.id}`}>{item.displayName}</Link>,
                <CountryName country={item.space.country} />,
                [
                  ...item.space.users.map((u, i) => {
                    const isAdmin = item.space.members.find(m => m.user.id === u.id && m.isAdmin);
                    return (
                      <Chip
                        key={i}
                        icon={isAdmin ? <SupervisorAccountIcon /> : <PersonIcon />}
                        label={u.name}
                        onClick={() => navigate(`/settings/users/${u.id}`)}
                      />
                    );
                  }),
                  ...item.space.members
                    .filter(m => item.space.users.findIndex(u => u.id === m.user.id) < 0)
                    .map((m, i) => {
                      return (
                        <Chip
                          key={item.space.users.length + i}
                          icon={m.isAdmin ? <SupervisorAccountIcon /> : <PersonIcon />}
                          label={m.user.name}
                          onClick={() => navigate(`/settings/users/${m.user.id}`)}
                        />
                      );
                    }),
                ],
                item.space.activeSEMPlan ? item.space.activeSEMPlan.semPlan.label : null,
              ]) || null
            }
            rowsLoading={loading}
            rowsErr={error}
            totalCount={data?.listSpacesPagedHierarchical.totalCount}
            defaultPageSize={10}
          />
        </Grid>
        {user.isSeminargo && (
          <Grid item xs={12}>
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/settings/spaces/_create')}
            >
              {i18next.t('spaces-list-add')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
}
